// Central mutable game context. The prototype relied on ~40 module-level globals that
// every subsystem read and reassigned; concentrating them in one object `G` keeps the
// port behaviourally identical while letting the code live in separate modules (arrays
// are reassigned via `G.objs = G.objs.filter(...)`, scalars via `G.score += ...`).
import type {
  DeathBy,
  Entity,
  GameState,
  Half,
  Hand,
  Mash,
  Particle,
  Point,
  TextFx,
  TrailPoint,
} from './types';

export interface Ctx {
  // viewport / DOM
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
  video: HTMLVideoElement;
  W: number;
  H: number;
  DPR: number;
  bgCanvas: HTMLCanvasElement | null;
  debug: boolean;

  // game state
  state: GameState;
  objs: Entity[];
  halves: Half[];
  parts: Particle[];
  texts: TextFx[];
  trail: TrailPoint[];
  score: number;
  resolved: number;
  missed: number;
  breaches: number;
  bestBatch: number;
  milestone: number;
  t: number;
  last: number;
  shake: number;
  frenzyUntil: number;
  finaleT: number;
  megaHits: number;
  deathBy: DeathBy;
  comboBuf: number[];
  comboTimer: number;
  spawnAcc: number;
  nextAngry: number;
  nextChute: number;
  secondChuteDone: boolean;
  volleyAcc: number;
  nextPom: number;
  pomDone: boolean;
  mash: Mash | null;
  pomCool: number;

  // audio
  AC: AudioContext | null;
  noiseBuf: AudioBuffer | null;

  // pointer input
  dragging: boolean;
  lastP: Point | null;
  megaCool: number;

  // camera / hand-tracking
  camMode: boolean;
  camReady: boolean;
  camErr: string | null;
  raiseFrames: number;
  landmarker: any;
  lastVT: number;
  detCount: number;
  detFps: number;
  detTick: number;
  detMs: number; // last hand-detection duration (ms) — GPU ~5-15ms, CPU fallback ~30-60ms
  camPath: string; // which detection path is live: 'worker' | 'main' | '' (debug readout)
  camHands: Hand[];
}

function freshHands(): Hand[] {
  return [
    { p: null, target: null, tv: { x: 0, y: 0 }, detAt: 0, lostAt: 0 },
    { p: null, target: null, tv: { x: 0, y: 0 }, detAt: 0, lostAt: 0 },
  ];
}

// DOM refs are assigned during init() (main.ts); non-null assertions keep call sites clean.
export const G: Ctx = {
  canvas: null!,
  ctx: null!,
  video: null!,
  W: 0,
  H: 0,
  DPR: 1,
  bgCanvas: null,
  debug: false,

  state: 'menu',
  objs: [],
  halves: [],
  parts: [],
  texts: [],
  trail: [],
  score: 0,
  resolved: 0,
  missed: 0,
  breaches: 0,
  bestBatch: 0,
  milestone: 100,
  t: 0,
  last: 0,
  shake: 0,
  frenzyUntil: -1,
  finaleT: 0,
  megaHits: 0,
  deathBy: null,
  comboBuf: [],
  comboTimer: 0,
  spawnAcc: 0,
  nextAngry: 6,
  nextChute: 12,
  secondChuteDone: false,
  volleyAcc: 0,
  nextPom: 16,
  pomDone: false,
  mash: null,
  pomCool: 0,

  AC: null,
  noiseBuf: null,

  dragging: false,
  lastP: null,
  megaCool: 0,

  camMode: false,
  camReady: false,
  camErr: null,
  raiseFrames: 0,
  landmarker: null,
  lastVT: -1,
  detCount: 0,
  detFps: 0,
  detTick: 0,
  detMs: 0,
  camPath: '',
  camHands: freshHands(),
};

// Start a fresh 60s shift. Mirrors the prototype's reset() exactly.
export function reset(): void {
  G.objs = [];
  G.halves = [];
  G.parts = [];
  G.texts = [];
  G.trail = [];
  G.comboBuf = [];
  G.score = 0;
  G.resolved = 0;
  G.missed = 0;
  G.breaches = 0;
  G.bestBatch = 0;
  G.milestone = 100;
  G.t = 0;
  G.shake = 0;
  G.frenzyUntil = -1;
  G.finaleT = 0;
  G.megaHits = 0;
  G.deathBy = null;
  G.spawnAcc = 0;
  G.nextAngry = 6;
  G.nextChute = 12;
  G.volleyAcc = 0;
  G.nextPom = 16 + Math.random() * 4;
  G.pomDone = false;
  G.mash = null;
  G.pomCool = 0;
  G.state = 'play';
}
